/**
 * TodayPageSkeleton Component
 *
 * Loading skeleton for the Today Page.
 * Mirrors the layout structure with animated placeholders.
 *
 * Atomic level: Template
 * Used by: TodayPage (loading state)
 */

import React from 'react';
import { Skeleton } from '../../ui/skeleton';
import { Card } from '../../ui/card';

/**
 * Loading skeleton for TodayPage.
 * Mirrors the layout structure with animated placeholders.
 *
 * Responsive layout:
 * - Mobile: Single column
 * - Tablet: 2-3 columns where appropriate
 * - Desktop: Full multi-column layout
 */
export function TodayPageSkeleton(): React.ReactElement {
  return (
    <div className="relative flex h-full min-h-0 flex-col overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(67,66,245,0.08),_transparent_28%),radial-gradient(circle_at_top_right,_rgba(7,160,195,0.08),_transparent_22%),linear-gradient(180deg,_#fbf9f6_0%,_#fffdf8_100%)]">
      <div className="relative flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-3 py-3 md:gap-5 md:px-6 md:py-5 xl:px-8 xl:py-6">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Skeleton className="h-7 w-7 rounded-lg" />
            <Skeleton className="h-6 w-16 rounded-full" />
            <Skeleton className="h-3 w-44" />
          </div>
          <Skeleton className="h-9 w-72 md:w-96" />
          <Skeleton className="h-4 w-full max-w-3xl" />
        </div>

        <div className="flex flex-wrap gap-3">
          <Skeleton className="h-9 w-28 rounded-full" />
          <Skeleton className="h-9 w-32 rounded-full" />
          <Skeleton className="h-9 w-32 rounded-full" />
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="gap-0 overflow-hidden rounded-[28px] border-border/70 bg-card/90 p-0 shadow-[var(--shadow-card)]" padding="sm">
              <div className="space-y-4 p-5">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="mx-auto h-28 w-28 rounded-full" />
                <Skeleton className="mx-auto h-5 w-24" />
                <div className="flex justify-center gap-4">
                  {[1, 2, 3].map((j) => (
                    <Skeleton key={j} className="h-3 w-12 rounded-full" />
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4">
          {[1].map((i) => (
            <Card key={i} className="gap-0 overflow-hidden rounded-[28px] border-border/70 bg-card/90 p-0 shadow-[var(--shadow-card)]" padding="sm">
              <div className="flex items-center justify-between border-b border-border/70 px-5 py-4">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-36" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="h-8 w-16 rounded-full" />
              </div>
              <div className="space-y-3 p-5">
                {[1, 2, 3].map((j) => (
                  <Skeleton key={j} className="h-12 rounded-xl" />
                ))}
              </div>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="gap-0 overflow-hidden rounded-[28px] border-border/70 bg-card/90 p-0 shadow-[var(--shadow-card)]" padding="sm">
              <div className="space-y-4 p-5">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-3 w-8" />
                </div>
                <div className="flex items-center gap-4">
                  <Skeleton className="h-16 w-16 rounded-full" />
                  <div className="flex-1 space-y-2">
                    {[1, 2, 3].map((j) => (
                      <Skeleton key={j} className="h-2 w-full" />
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
